import React, { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useApi } from '@/hooks/useApi';
import { toast } from 'sonner';
import {
  Link2, CheckCircle2, XCircle, RefreshCw, Key, Download,
  AlertTriangle, Wifi, WifiOff, Plus, Trash2, Building2,
  CalendarDays, ChevronDown, ChevronUp
} from 'lucide-react';

export const Beds24ConfigPage = () => {
  const api = useApi();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [adding, setAdding] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [expandedId, setExpandedId] = useState(null);

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await api.get('/beds24/accounts');
      setAccounts(res.data || []);
    } catch {
      toast.error('Erreur lors du chargement des comptes');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const handleAdd = async () => {
    if (!newName.trim() || !newCode.trim()) {
      toast.error('Nom et code d\'invitation requis');
      return;
    }
    setAdding(true);
    try {
      const res = await api.post('/beds24/accounts', { name: newName, invite_code: newCode });
      toast.success(res.data.message);
      setNewName('');
      setNewCode('');
      setShowAddForm(false);
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Supprimer le compte "${name}" ? Les données synchronisées seront conservées.`)) return;
    try {
      await api.delete(`/beds24/accounts/${id}`);
      toast.success(`Compte "${name}" supprimé`);
      fetchAccounts();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSync = async (id) => {
    setSyncingId(id);
    try {
      const res = await api.post(`/beds24/accounts/${id}/sync`);
      toast.success(res.data.message);
      setTimeout(fetchAccounts, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de synchronisation');
    } finally {
      setSyncingId(null);
    }
  };

  const handleTest = async (id) => {
    setTestingId(id);
    try {
      const res = await api.post(`/beds24/accounts/${id}/test`);
      setTestResults(prev => ({ ...prev, [id]: res.data }));
      if (res.data.connected) toast.success('Connexion validée');
      else toast.warning('Connexion échouée');
    } catch {
      setTestResults(prev => ({ ...prev, [id]: { connected: false, message: 'Erreur de test' } }));
    } finally {
      setTestingId(null);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <Header title="Configuration Beds24" />
        <div className="p-6 flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-pulse-soft text-slate-400">Chargement...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Header title="Configuration Beds24" subtitle="Gérez vos comptes PMS Beds24" />

      <div className="p-6 space-y-6 animate-fadeIn max-w-4xl" data-testid="beds24-config">

        {/* Summary */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-white border-slate-100">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FFF0F1] flex items-center justify-center">
                <Key className="h-5 w-5 text-[#FF5A6E]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{accounts.length}</p>
                <p className="text-xs text-slate-500">Compte{accounts.length > 1 ? 's' : ''} Beds24</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-100">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                <Wifi className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{accounts.filter(a => a.is_connected).length}</p>
                <p className="text-xs text-slate-500">Connecté{accounts.filter(a => a.is_connected).length > 1 ? 's' : ''}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-100">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{accounts.reduce((s, a) => s + (a.properties_count || 0), 0)}</p>
                <p className="text-xs text-slate-500">Propriétés totales</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Account list */}
        <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Link2 className="h-5 w-5 text-[#FF5A6E]" />
                Comptes Beds24
              </CardTitle>
              <CardDescription>Chaque compte correspond à un client ou un portfolio Beds24</CardDescription>
            </div>
            <Button
              onClick={() => setShowAddForm(!showAddForm)}
              className="bg-[#FF5A6E] hover:bg-[#FF3B55] text-white"
              data-testid="add-account-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              Ajouter un compte
            </Button>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Add form */}
            {showAddForm && (
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3" data-testid="add-account-form">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Nom du compte</Label>
                    <Input
                      value={newName}
                      onChange={e => setNewName(e.target.value)}
                      placeholder="Ex: Cledici Conciergerie"
                      data-testid="account-name-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Code d'invitation Beds24</Label>
                    <Input
                      value={newCode}
                      onChange={e => setNewCode(e.target.value)}
                      placeholder="Collez le code ici..."
                      className="font-mono text-sm"
                      data-testid="account-code-input"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  Beds24 : Apps & Integrations &rarr; API &rarr; Generate invite code
                </p>
                <div className="flex gap-2">
                  <Button onClick={handleAdd} disabled={adding} className="bg-[#FF5A6E] hover:bg-[#FF3B55] text-white" data-testid="confirm-add-btn">
                    {adding ? <><RefreshCw className="mr-2 h-4 w-4 animate-spin" />Connexion...</> : <><Link2 className="mr-2 h-4 w-4" />Connecter</>}
                  </Button>
                  <Button onClick={() => setShowAddForm(false)} variant="outline">Annuler</Button>
                </div>
              </div>
            )}

            {/* Accounts */}
            {accounts.length === 0 ? (
              <div className="text-center py-10 text-slate-400">
                <Key className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                <p className="text-sm">Aucun compte Beds24 configuré</p>
                <p className="text-xs mt-1">Cliquez "Ajouter un compte" pour commencer</p>
              </div>
            ) : (
              <div className="space-y-3">
                {accounts.map(acc => (
                  <div
                    key={acc.id}
                    className="border border-slate-200 rounded-xl overflow-hidden transition-all"
                    data-testid={`account-${acc.id}`}
                  >
                    {/* Account header */}
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => setExpandedId(expandedId === acc.id ? null : acc.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${acc.is_connected ? 'bg-emerald-400' : 'bg-red-400'}`} />
                        <div>
                          <p className="font-semibold text-slate-900">{acc.name}</p>
                          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                            <span className="flex items-center gap-1"><Building2 size={11} />{acc.properties_count} propriétés</span>
                            <span className="flex items-center gap-1"><CalendarDays size={11} />{acc.bookings_count} réservations</span>
                            {acc.last_sync && (
                              <span>Sync: {new Date(acc.last_sync).toLocaleDateString('fr-FR')}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          acc.is_connected ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
                        }`}>
                          {acc.is_connected ? 'Connecté' : 'Déconnecté'}
                        </span>
                        {expandedId === acc.id ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                      </div>
                    </div>

                    {/* Expanded actions */}
                    {expandedId === acc.id && (
                      <div className="px-4 pb-4 pt-0 border-t border-slate-100 space-y-3">
                        <div className="flex flex-wrap gap-2 pt-3">
                          <Button
                            onClick={() => handleTest(acc.id)}
                            disabled={testingId === acc.id}
                            variant="outline"
                            size="sm"
                            className="text-blue-600 border-blue-200"
                            data-testid={`test-${acc.id}`}
                          >
                            {testingId === acc.id ? <RefreshCw className="mr-1 h-3 w-3 animate-spin" /> : <CheckCircle2 className="mr-1 h-3 w-3" />}
                            Tester
                          </Button>
                          <Button
                            onClick={() => handleSync(acc.id)}
                            disabled={syncingId === acc.id}
                            size="sm"
                            className="bg-[#FF5A6E] hover:bg-[#FF3B55] text-white"
                            data-testid={`sync-${acc.id}`}
                          >
                            {syncingId === acc.id ? <RefreshCw className="mr-1 h-3 w-3 animate-spin" /> : <Download className="mr-1 h-3 w-3" />}
                            Synchroniser
                          </Button>
                          <Button
                            onClick={() => handleDelete(acc.id, acc.name)}
                            variant="outline"
                            size="sm"
                            className="text-red-500 border-red-200 hover:bg-red-50"
                            data-testid={`delete-${acc.id}`}
                          >
                            <Trash2 className="mr-1 h-3 w-3" />
                            Supprimer
                          </Button>
                        </div>

                        {/* Test result */}
                        {testResults[acc.id] && (
                          <div className={`p-3 rounded-lg text-sm ${
                            testResults[acc.id].connected ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'
                          }`}>
                            <div className="flex items-center gap-2">
                              {testResults[acc.id].connected ? (
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              ) : (
                                <XCircle className="h-4 w-4 text-red-600" />
                              )}
                              <span className={testResults[acc.id].connected ? 'text-emerald-700' : 'text-red-700'}>
                                {testResults[acc.id].message}
                              </span>
                              {testResults[acc.id].properties_count !== undefined && (
                                <span className="text-emerald-600 text-xs">({testResults[acc.id].properties_count} propriétés)</span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Details */}
                        <div className="grid grid-cols-2 gap-3 text-xs text-slate-500 bg-slate-50 rounded-lg p-3">
                          <div>
                            <span className="font-medium text-slate-600">Token expire</span>
                            <p>{acc.token_expires_at ? new Date(acc.token_expires_at).toLocaleString('fr-FR') : 'N/A'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-slate-600">Créé le</span>
                            <p>{new Date(acc.created_at).toLocaleDateString('fr-FR')}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info */}
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Multi-comptes Beds24</p>
                <ul className="list-disc list-inside space-y-1 text-amber-700 text-xs">
                  <li>Chaque compte Beds24 correspond à un portfolio de propriétés distinct</li>
                  <li>Les données sont synchronisées indépendamment pour chaque compte</li>
                  <li>Le code d'invitation se génère dans Beds24 : Apps & Integrations &rarr; API</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default Beds24ConfigPage;
